import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeSlash, User, EnvelopeSimple, LockKey, Lightning } from '@phosphor-icons/react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import logger from '../services/logger';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, login, register, checkAuth } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Auto-redirect already-authenticated users to the dashboard
  useEffect(() => {
    if (!authLoading && user) navigate('/dashboard', { replace: true });
  }, [authLoading, user, navigate]);
  
  // Detect if we're on /login or /register
  useEffect(() => {
    setIsLogin(location.pathname === '/login');
  }, [location.pathname]);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Read & validate referral code from localStorage (set by LandingPage when ?ref=XXX).
  const [refInfo, setRefInfo] = useState(null);
  useEffect(() => {
    let code = '';
    try { code = localStorage.getItem('obgh_ref_code') || ''; } catch (_) {}
    if (!code || isLogin) return;
    // Validate it (public endpoint, no auth required)
    api.get(`/referrals/validate?code=${encodeURIComponent(code)}`)
      .then(res => {
        if (res.data?.valid) setRefInfo({ code, ...res.data });
        else { try { localStorage.removeItem('obgh_ref_code'); } catch (_) {} }
      })
      .catch(() => {});
  }, [isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        if (!acceptedTerms) {
          toast.error('Vous devez accepter les conditions d\'utilisation');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
      }
      toast.success(isLogin ? 'Connexion réussie!' : 'Compte créé avec succès!');
      navigate('/dashboard');
    } catch (error) {
      logger.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-obsidian text-white font-body flex items-center justify-center p-6 relative overflow-hidden">
      {/* Tactical Grid Background */}
      <div className="absolute inset-0 tactical-grid opacity-20" />
      
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/32145292/pexels-photo-32145292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/85 to-black/95" />
      </div>

      {/* Auth Container */}
      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/obgh-logo-v2.png" alt="OBGH" className="h-16 w-auto" />
          </div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-blaze block mb-2">TERRITOIRE PRO</span>
          <h1 className="text-3xl font-heading font-black uppercase tracking-tight">
            {isLogin ? 'CONNEXION' : 'INSCRIPTION'}
          </h1>
        </div>

        {/* Form Card */}
        <div className="scanlines tactical-border bg-obsidian-card p-8 relative">
          {/* Scan beam */}
          <div className="absolute top-0 left-0 w-full h-0.5 bg-blaze/50 animate-scan pointer-events-none" />

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Referral badge (only on register, when ref code present and valid) */}
            {!isLogin && refInfo && (
              <div className="border border-[#ff6b35]/40 bg-[#ff6b35]/10 rounded-sm p-3 flex items-start gap-3" data-testid="referral-badge">
                <span className="text-2xl flex-shrink-0">🎁</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#ff6b35]">Tu as été parrainé{refInfo.first_name ? ` par ${refInfo.first_name}` : ''}</div>
                  <div className="text-[11px] text-zinc-300 mt-0.5">
                    Reçois <strong className="text-[#ff6b35]">-{refInfo.discount_pct}%</strong> sur ton premier forfait payant (Pro, Elite ou Ultra).
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1 font-mono">Code : {refInfo.code}</div>
                </div>
              </div>
            )}

            {/* Name Field (Register only) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-mono uppercase tracking-wider text-zinc-400 mb-2">
                  <User size={16} className="inline mr-2" weight="duotone" />
                  Nom complet
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-obsidian border border-white/20 text-white px-4 py-3 focus:border-blaze focus:outline-none transition-all font-body"
                  placeholder="Jean-Pierre Tremblay"
                  required={!isLogin}
                />
              </div>
            )}

            {/* Email Field */}
            <div>
              <label className="block text-sm font-mono uppercase tracking-wider text-zinc-400 mb-2">
                <EnvelopeSimple size={16} className="inline mr-2" weight="duotone" />
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-obsidian border border-white/20 text-white px-4 py-3 focus:border-blaze focus:outline-none transition-all font-body"
                placeholder="votre@email.com"
                required
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-mono uppercase tracking-wider text-zinc-400 mb-2">
                <LockKey size={16} className="inline mr-2" weight="duotone" />
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-obsidian border border-white/20 text-white px-4 py-3 pr-12 focus:border-blaze focus:outline-none transition-all font-body"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-blaze transition-colors"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Terms Checkbox (Register only) */}
            {!isLogin && (
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                  data-testid="terms-checkbox"
                  className={`flex-shrink-0 w-5 h-5 mt-0.5 border rounded-sm flex items-center justify-center transition-all ${
                    acceptedTerms
                      ? 'bg-blaze border-blaze'
                      : 'border-white/30 hover:border-blaze/60'
                  }`}
                >
                  {acceptedTerms && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                </button>
                <label className="text-xs text-zinc-400 leading-relaxed cursor-pointer" onClick={() => setAcceptedTerms(!acceptedTerms)}>
                  J'accepte les{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blaze hover:underline font-bold"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="terms-link-register"
                  >
                    conditions d'utilisation
                  </a>
                  {' '}et la{' '}
                  <a
                    href="/terms#privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blaze hover:underline font-bold"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="privacy-link-register"
                  >
                    politique de confidentialite
                  </a>
                </label>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-tactical py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black"></div>
                  Chargement...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Lightning size={20} weight="fill" />
                  {isLogin ? 'SE CONNECTER' : 'CRÉER MON COMPTE'}
                </span>
              )}
            </button>
          </form>

          {/* Toggle Login/Register */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setFormData({ email: '', password: '', name: '' });
              }}
              className="text-sm text-zinc-400 hover:text-blaze transition-colors font-mono uppercase tracking-wider"
            >
              {isLogin ? (
                <>Pas de compte? <span className="text-blaze font-bold">CRÉER UN COMPTE →</span></>
              ) : (
                <>Déjà inscrit? <span className="text-blaze font-bold">SE CONNECTER →</span></>
              )}
            </button>
          </div>

          {/* Terms link */}
          <div className="mt-4 text-center">
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-zinc-500 hover:text-blaze transition-colors font-mono uppercase tracking-wider"
              data-testid="terms-link-auth"
            >
              Conditions d'utilisation & Confidentialite
            </a>
          </div>

          {/* Test credentials removed for security */}
        </div>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-zinc-500 hover:text-zinc-300 transition-colors font-mono uppercase tracking-wider"
          >
            ← RETOUR À L'ACCUEIL
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
